import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Chip, FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, TextField, Typography } from "@mui/material"
import { makeStyles } from "@mui/styles";
import FormMonitoring from "components/FormMonitoring"
import { CommonErrorsTexts, CommonTexts, professionalTexts } from "locale/es"
import { useState } from "react"
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux"
import { AppColors } from "resources/AppColors";
import { RolesManager } from "utils/RolesManager";
import { z } from "zod";

const useStyles = makeStyles((theme) => ({
  textFieldLabel: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: AppColors.PRIMARY,
      },
    },
    "& .MuiInputBase-root": {
      color: AppColors.PRIMARY,
    },
    "& .MuiInputLabel-root": {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
}));

const passwordType = z.string().superRefine((val, ctx) => {
  if (val.length > 0 && val.length < 8) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      type: "string",
      message: professionalTexts.formInputErrors.password,
    });
    return z.NEVER
  }
})

const roleOptions = ["ROLE_ADMIN", "ROLE_CONTENT_MANAGER", "ROLE_HEALTHCARE"]

const roleDict = {
  ROLE_ADMIN: "Admin",
  ROLE_CONTENT_MANAGER: "Content Manager",
  ROLE_HEALTHCARE: "HealthCare Worker"
}

const ProfessionalSchema = z.object({
  authorities: z.array(z.enum(Object.keys(roleDict))).nonempty(),
  centerIds: z.array(z.string()).nonempty(),
  email: z.string().min(1, CommonErrorsTexts.required).email({ message: professionalTexts.formInputErrors.email }),
  password: passwordType,
  confirmPassword: z.string(),
  firstName: z.string().min(2, professionalTexts.formInputErrors.firstName),
  lastName: z.string().min(2, professionalTexts.formInputErrors.lastName),
  organization: z.string().optional(),
  profile: z.string().optional(),
})
  .refine((data) => data.password === data.confirmPassword, {
    message: professionalTexts.formInputErrors.confirmPassword,
    path: ["confirmPassword"],
  })
  .transform((data) => ({
    ...data,
    login: data.email,
    password: data.password || null
  }))

const ProfessionalForm = ({ onSubmit, setShowModal, selectedProfessional }) => {
  const [formError, setFormError] = useState(undefined);
  const { user } = useSelector(state => state.userAuthentication)
  const centerId = useSelector(({ center }) => center)?.data?.id
  const allCenters = useSelector(({ centers }) => centers?.data)

  // * If user is super admin, use all the centers, else only use the user centers
  const userCenters = allCenters || user.centers

  const centers = userCenters.reduce((centers, currentCenter) => ({
    ...centers,
    [currentCenter.id]: currentCenter.name,
  }), {})

  const centersValues = Object.keys(centers)

  const selectedProfessionalAuthorities = selectedProfessional?.user?.authorities
    ? selectedProfessional?.user?.authorities
      .map(role => role.name)
      .filter(role => roleOptions.some(option => option === role))
    : undefined

  const selectedProfessionalCentersIds = selectedProfessional?.centers
    ? selectedProfessional?.centers
      .map(center => center.id)
      .filter(center => centersValues.some(userCenter => userCenter === center))
    : undefined

  const { handleSubmit, control, reset } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: zodResolver(ProfessionalSchema),
    resetOptions: {
      keepDefaultValues: true,
    },
    defaultValues: {
      authorities: selectedProfessionalAuthorities || [],
      centerIds: selectedProfessionalCentersIds || [centerId],
      email: selectedProfessional?.user?.email || "",
      password: "",
      confirmPassword: "",
      firstName: selectedProfessional?.user?.firstName || "",
      lastName: selectedProfessional?.user?.lastName || "",
      organization: selectedProfessional?.organization || "",
      profile: selectedProfessional?.profile || "",
    },
  });

  const classes = useStyles();

  const dispatch = useDispatch()

  const handleOnSubmit = async (data) => {
    try {
      await dispatch(onSubmit({ ...data, id: selectedProfessional?.id }))
      setShowModal(false)
    } catch (error) {
      setFormError(error);
    }
  };

  return (
    <FormMonitoring
      submitForm={handleSubmit(data => handleOnSubmit(data))}
      resetFormInputs={reset}
      isEdit={Boolean(selectedProfessional)}
    >
      <Controller
        control={control}
        name='firstName'
        render={({
          field: { value, onChange, onBlur, ref },
          fieldState: { error },
        }) => (
          <TextField
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            inputRef={ref}
            type='text'
            InputLabelProps={{
              required: true
            }}
            label={CommonTexts.NAME}
            margin='normal'
            variant='outlined'
            autoFocus
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ""}
          />
        )}
      />

      <Controller
        control={control}
        name='lastName'
        render={({
          field: { value, onChange, onBlur, ref },
          fieldState: { error },
        }) => (
          <TextField
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            inputRef={ref}
            type='text'
            InputLabelProps={{
              required: true
            }}
            label={CommonTexts.SURNAME}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ""}
          />
        )}
      />

      <Controller
        control={control}
        name='email'
        render={({
          field: { onChange, onBlur, value, ref },
          fieldState: { error }
        }) => (
          <TextField
            type='email'
            value={value}
            inputRef={ref}
            InputLabelProps={{
              required: true
            }}
            onChange={onChange}
            onBlur={onBlur}
            label={CommonTexts.EMAIL}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
            autoComplete='off'
            error={Boolean(error)}
            helperText={error?.message ?? ""} />
        )}
      />

      <Controller
        control={control}
        name='organization'
        render={({
          field,
          fieldState: { error },
        }) => (
          <TextField
            {...field}
            type='text'
            label={CommonTexts.ORGANIZATION}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ""}
          />
        )}
      />

      <Controller
        control={control}
        name='profile'
        render={({
          field,
          fieldState: { error },
        }) => (
          <TextField
            {...field}
            type='text'
            label={CommonTexts.PROFILE}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ""}
          />
        )}
      />

      <Controller
        control={control}
        name='authorities'
        render={({
          field: { onChange, onBlur, value, ref },
          fieldState: { error },
        }) => (
          <FormControl sx={{ mt: 2, mb: 1 }} className={classes.textFieldLabel}>
            <InputLabel required id="select-authorities-label">{CommonTexts.ROLE}</InputLabel>
            <Select
              labelId="authorities-selector-label"
              id="authorities-selector"
              variant="outlined"
              multiple
              label={CommonTexts.ROLE}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              inputRef={ref}
              error={Boolean(error)}
              input={<OutlinedInput id="select-multiple" label="Chip" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((option) => (
                    <Chip key={option} label={roleDict[option]} sx={{ textTransform: "capitalize" }} />
                  ))}
                </Box>
              )}
            >
              {roleOptions.map(option => <MenuItem key={option} value={option} sx={{ textTransform: "capitalize" }}>
                {roleDict[option]}
              </MenuItem>
              )}
            </Select>
            <FormHelperText
              id="my-helper-text"
              error={Boolean(error)}
            >
              {error && professionalTexts.formInputErrors.role}
            </FormHelperText>
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name='password'
        render={({
          field,
          fieldState: { error },
        }) => (
          <TextField
            {...field}
            type='password'
            label={CommonTexts.PASSWORD}
            margin='normal'
            variant='outlined'
            autoComplete='new-password'
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ""} />
        )}
      />
      <Controller
        control={control}
        name='confirmPassword'
        render={({
          field: { value, onChange, onBlur, ref },
          fieldState: { error },
        }) => (
          <TextField
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            inputRef={ref}
            type='password'
            label={CommonTexts.CONFIRM_PASSWORD}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ""} />
        )}
      />
      <Controller
        control={control}
        name='centerIds'
        render={({
          field: { onChange, onBlur, value, ref },
          fieldState: { error }
        }) => (
          <FormControl sx={{ mt: 2, mb: 1 }} className={classes.textFieldLabel}>
            <InputLabel
              required
              id="select-centers-label"
              sx={{ backgroundColor: AppColors.WHITE }}
            >
              {CommonTexts.CENTERS}
            </InputLabel>
            <Select
              labelId="centers-selector-label"
              id="centers-selector"
              variant="outlined"
              multiple
              label={CommonTexts.CENTERS}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              inputRef={ref}
              error={Boolean(error)}
              input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((option) => (
                    <Chip key={option} label={centers[option]} />
                  ))}
                </Box>
              )}
            >
              {centersValues.map(centerId => <MenuItem key={centerId} value={centerId}>
                {centers[centerId]}
              </MenuItem>
              )}
            </Select>
            <FormHelperText
              error={Boolean(error)}
            >
              {error && professionalTexts.formInputErrors.center}
            </FormHelperText>
          </FormControl>
        )}
      />
      <Typography mt={1} variant='body' sx={{ color: "red" }}>
        {formError && professionalTexts.formInputErrors.api_error}
      </Typography>
    </FormMonitoring>
  )
}

export default ProfessionalForm
